import { TOrbit } from '../../i18n/TModules';
import { filledRows, windowErrors } from '../configStudio/configModel';
import { AdapterContext, limitValue, positive } from '../wizard/adapters/common';
import { toCampaignRule, Translate, triggerErrors } from '../wizard/adapters/ruleAdapter';
import { LimitPeriod, WizardCampaignType } from '../wizard/wizardModel';
import { RuleWizardState } from './ruleModel';

const TR = `${TOrbit}.configStudio.rule`;
const TE = `${TOrbit}.configStudio.errors`;

// `toCampaignRule` adds type keys for stamp and progress only; quest adds none, so its rule is the
// bare trigger a transaction rule carries too.
const BARE_TRIGGER_TYPE: WizardCampaignType = 'quest';

/** A transaction rule as the module's rule form (`Rule/form-v2`) holds it before its own submit. */
export type TransactionRuleValues = Record<string, unknown> & {
  name: string;
  temp: Record<string, unknown>;
  conditions: unknown[];
};

// The module's rule limits take plural periods (`validationSchema.tsx` `account_interval_period`).
const PLURAL: Record<LimitPeriod, string> = {
  day: 'days',
  week: 'weeks',
  month: 'months',
  year: 'years'
};

const triggerValues = (state: RuleWizardState, t: Translate) => {
  if (state.triggerKind === 'customer') {
    return {
      temp: { trigger_type: 'customer', event_name: state.customerEvent },
      event_name: state.customerEvent,
      event_params: {},
      conditions: []
    };
  }
  const { temp, event_name, event_params, conditions } = toCampaignRule(
    state.trigger,
    BARE_TRIGGER_TYPE,
    t
  );
  return { temp: { ...temp }, event_name, event_params, conditions };
};

const outcomeValues = (state: RuleWizardState): Record<string, unknown> => {
  const { outcome } = state;
  switch (outcome.kind) {
    case 'stamp':
    case 'game':
      return {
        outcome_type: 'Campaign',
        outcome_id: outcome.campaign?.id ?? null,
        outcome_value: Number(outcome.quantity)
      };
    case 'reward':
      return { outcome_type: 'Reward::Campaign', outcome_id: outcome.reward?.id ?? null };
    case 'points':
      return {
        outcome_type: 'StoredValue::Campaign',
        outcome_id: outcome.loyalty?.id ?? null,
        outcome_value: Number(outcome.points)
      };
    case 'audience':
      return { outcome_type: 'Audience', outcome_id: outcome.audience?.id ?? null };
    case 'customer':
      return {
        outcome_type: 'User::Account',
        outcome: {
          outcome_options: {
            outcome_properties: filledRows(outcome.properties).map(row => ({
              key: row.key.trim(),
              value: row.value.trim()
            }))
          }
        }
      };
    case 'push':
      return {
        outcome_type: 'Push Event Only',
        outcome_options: {
          ...(outcome.mediaTag === null ? {} : { selected_media_type_id: outcome.mediaTag }),
          custom_fields: Object.fromEntries(
            filledRows(outcome.payload).map(row => [row.key.trim(), row.value.trim()])
          )
        }
      };
    default:
      return { outcome_type: null };
  }
};

const interval = (
  prefix: string,
  maxKey: string,
  on: boolean,
  value: string,
  period: LimitPeriod
) =>
  on && positive(value)
    ? { [maxKey]: Number(value), [`${prefix}_units`]: 1, [`${prefix}_period`]: PLURAL[period] }
    : { [maxKey]: null };

/** "Only the first qualifying transaction" is a per-person limit of 1 (no other key exists). */
const accountLimit = (state: RuleWizardState) => {
  const perPerson = limitValue(state.limits.perPersonOn, state.limits.perPerson);
  return state.firstOnly ? 1 : perPerson;
};

export const buildRuleValues = (
  state: RuleWizardState,
  context: AdapterContext
): TransactionRuleValues => {
  const { limits, window } = state;
  const trigger = triggerValues(state, context.t);
  const outcome = outcomeValues(state);
  const name = state.name.trim();
  return {
    name,
    name_en: name,
    rule_type: 'trigger',
    begins_at: state.beginsAt ?? context.now(),
    ends_at: state.endsOn ? state.endsAt : null,
    event_name: trigger.event_name,
    event_params: trigger.event_params,
    conditions: trigger.conditions,
    ...outcome,
    temp: {
      ...trigger.temp,
      rule_trigger_checked: window.on,
      ...(state.outcome.kind === 'stamp' || state.outcome.kind === 'game'
        ? { outcome_campaign_type: state.outcome.kind }
        : {})
    },
    rule_trigger_days: window.on
      ? [
          { attribute: 'transaction_date', operator: 'day_of_week', selected_values: window.days },
          {
            attribute: 'transaction_time',
            operator: 'greater_than_or_equal_to',
            selected_values: window.from
          },
          {
            attribute: 'transaction_time',
            operator: 'less_than_or_equal_to',
            selected_values: window.to
          }
        ]
      : null,
    trigger_limit: limitValue(limits.totalOn, limits.total),
    account_limit: accountLimit(state),
    ...interval(
      'rule_trigger_interval',
      'rule_trigger_interval_max_available',
      limits.rateOn,
      limits.rate,
      limits.ratePeriod
    ),
    ...interval(
      'account_interval',
      'account_interval_limit',
      limits.userRateOn,
      limits.userRate,
      limits.userRatePeriod
    ),
    timezone: context.timezone
  };
};

const triggerDetailErrors = (state: RuleWizardState, t: Translate): string[] => {
  if (state.triggerKind === 'customer') {
    return [];
  }
  if (state.triggerKind === 'loyalty' && state.trigger.loyalty === null) {
    return [t(`${TR}.details.selectLoyalty`)];
  }
  return triggerErrors(state.trigger, BARE_TRIGGER_TYPE, t);
};

const outcomeErrors = (state: RuleWizardState, t: Translate): string[] => {
  const { outcome } = state;
  const D = `${TR}.outcomeDetails`;
  const errors: string[] = [];
  switch (outcome.kind) {
    case 'stamp':
    case 'game':
      if (outcome.campaign === null) errors.push(t(`${D}.campaignRequired`));
      if (!positive(outcome.quantity)) errors.push(t(`${D}.quantityRequired`));
      break;
    case 'reward':
      if (outcome.reward === null) errors.push(t(`${D}.rewardRequired`));
      break;
    case 'points':
      if (!positive(outcome.points)) errors.push(t(`${D}.pointsRequired`));
      if (outcome.loyalty === null) errors.push(t(`${D}.loyaltyRequired`));
      break;
    case 'audience':
      if (outcome.audience === null) errors.push(t(`${D}.audienceRequired`));
      break;
    case 'customer':
      if (filledRows(outcome.properties).length === 0) errors.push(t(`${D}.propertiesRequired`));
      break;
    default:
      break;
  }
  return errors;
};

const scheduleErrors = (state: RuleWizardState, t: Translate): string[] => {
  const S = `${TR}.schedule`;
  const errors: string[] = [];
  if (state.endsOn) {
    if (state.endsAt === null) {
      errors.push(t(`${S}.endRequired`));
    } else if (state.beginsAt !== null && state.endsAt <= state.beginsAt) {
      errors.push(t(`${TE}.dateOrder`));
    }
  }
  errors.push(
    ...windowErrors(state.window, {
      days: t(`${TE}.daysRequired`),
      time: t(`${TE}.required`, { field: t(`${TOrbit}.configStudio.timeWindow`) }),
      order: t(`${TE}.timeOrder`)
    })
  );
  const { limits } = state;
  const checked: [boolean, string, string][] = [
    [limits.totalOn, limits.total, `${S}.total`],
    [limits.perPersonOn, limits.perPerson, `${S}.perPerson`],
    [limits.rateOn, limits.rate, `${S}.rate`],
    [limits.userRateOn, limits.userRate, `${S}.userRate`]
  ];
  checked
    .filter(([on, value]) => on && !positive(value))
    .forEach(([, , key]) => errors.push(t(`${TE}.positive`, { field: t(key) })));
  return errors;
};

/** What still blocks Next on the current step; tile picks never block. */
export const ruleStepErrors = (state: RuleWizardState, t: Translate): string[] => {
  switch (state.step) {
    case 'triggerDetails':
      return triggerDetailErrors(state, t);
    case 'outcomeDetails':
      return outcomeErrors(state, t);
    case 'schedule':
      return scheduleErrors(state, t);
    default:
      return [];
  }
};
